import { useRef, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { MAILTO } from "@/lib/site";

const SLIDES = [
  { src: "/products/VA1.mp4", webm: "/products/VA1.webm", poster: "/products/VA1-poster.jpg", alt: "Aseptic processing of sea buckthorn at HA facility in Ladakh", video: true },
  { src: "/products/VA2.mp4", webm: "/products/VA2.webm", poster: "/products/VA2-poster.jpg", alt: "Sea buckthorn whole berry oil sample", video: true },
  { src: "/products/VA3.mp4", webm: "/products/VA3.webm", poster: "/products/VA3-poster.jpg", alt: "Sea buckthorn gummies", video: true },
  { src: "/products/VA4.mp4", webm: "/products/VA4.webm", poster: "/products/VA4-poster.jpg", alt: "Sea buckthorn fresh berries on the branch", video: true },
  { src: "/products/VA5.jpg", alt: "Dried sea buckthorn berries", video: false },
  { src: "/products/VA6.jpg", alt: "Sea buckthorn tea leaves", video: false },
  { src: "/products/VA7.jpg", alt: "Sea buckthorn powder", video: false },
];

const ROWS = [
  {
    product: "Sea Buckthorn Fruit Oil",
    specs: [
      "Extraction method: Supercritical CO₂",
      "USDA Organic support where applicable",
      "Typical omega-7 / palmitoleic acid profile available on request",
      "PV, AV and fatty acid profile available",
    ],
    benefits: [
      "Skin / hair / nail positioning",
      "Mucous membrane support",
      "Beauty-from-within",
      "Metabolic wellness positioning",
    ],
    apps: ["Softgels", "Dietary supplements", "Food applications", "Cosmetic formulations"],
  },
  {
    product: "Sea Buckthorn Seed Oil",
    specs: [
      "Extraction method: Supercritical CO₂",
      "Vitamin E and ALA testing available",
      "Fatty acid profile, PV and AV available",
      "USDA Organic support where applicable",
    ],
    benefits: [
      "Skin and soft tissue positioning",
      "Anti-aging positioning",
      "Calming / irritation positioning",
      "Beauty treatment formulations",
    ],
    apps: ["Skin care", "Cosmetics", "Softgel supplements", "Dietary supplements"],
  },
  {
    product: "Sea Buckthorn Whole Berry Oil",
    specs: [
      "Extraction method: Supercritical CO₂",
      "Combined fruit + seed oil profile",
      "Fatty acid profile and oxidative markers available",
      "USDA Organic support where applicable",
    ],
    benefits: [
      "Broad omega 3/6/7/9 positioning",
      "Beauty-from-within",
      "Antioxidant support",
      "Skin and mucous membrane support",
    ],
    apps: ["Softgels", "Beverage shots", "Nutraceuticals", "Cosmetic blends"],
  },
  {
    product: "Sea Buckthorn Pulp / Purée",
    specs: [
      "100% pulped berries format available",
      "Brix, pH and preservative status available",
      "Microbiology and heavy metals testing",
      "USDA Organic support where applicable",
    ],
    benefits: [
      "Skin / hair / nail positioning",
      "Cardiovascular wellness positioning",
      "Mucous membrane support",
      "Functional beverage use",
    ],
    apps: ["Juice", "Supplements", "Food ingredient", "Wellness shots", "Concentrates"],
  },
  {
    product: "Sea Buckthorn Powder",
    specs: [
      "Freeze-dried / spray-dried options where available",
      "Carrier declaration",
      "Moisture, mesh and micro testing",
      "Bulk and sachet-ready formats",
    ],
    benefits: [
      "Clean-label dry blend positioning",
      "Travel-friendly wellness",
      "Functional food and supplement use",
    ],
    apps: ["Capsules", "Gummies", "Sachets", "Dry drink mixes", "Smoothie powders"],
  },
  {
    product: "Sea Buckthorn Dried Berries / Leaves",
    specs: [
      "Whole dried berry and leaf formats",
      "Moisture, microbiology and heavy metals testing",
      "USDA Organic support where applicable",
      "Bulk and food-grade packaging",
    ],
    benefits: ["Functional food positioning", "Tea / infusion positioning", "Snack-ready functional ingredient"],
    apps: ["Tea blends", "Trail mix / snacks", "Food applications", "Herbal infusions"],
  },
];

export default function ProductMatrix() {
  const trackRef = useRef(null);
  const scroll = (dir) =>
    trackRef.current?.scrollBy({ left: dir * 320, behavior: "smooth" });

  // Safety net for strict autoplay policies (iOS Safari, low-power mode):
  // explicitly call play() once each video has metadata.
  useEffect(() => {
    const vids = trackRef.current?.querySelectorAll("video") || [];
    vids.forEach((v) => {
      v.muted = true;
      v.playsInline = true;
      const tryPlay = () => v.play().catch(() => {});
      if (v.readyState >= 2) tryPlay();
      else v.addEventListener("loadeddata", tryPlay, { once: true });
    });
  }, []);

  return (
    <section id="specs" className="section section-white" data-testid="specs-section">
      <div className="wrap">
        <div className="section-head">
          <div className="eyebrow">Product Matrix</div>
          <h2>Features, specs, benefits and applications.</h2>
          <p className="copy" style={{ marginTop: 12 }}>
            Each product is supported with product specifications, batch testing and buyer documentation as applicable.
          </p>
        </div>

        <div className="format-block">
          <div className="format-title">
            <h3>From Berry to Buyer-Ready Formats</h3>
          </div>
          <div className="carousel-shell" data-testid="format-carousel">
            <button
              className="carousel-btn carousel-left"
              type="button"
              aria-label="Scroll formats left"
              onClick={() => scroll(-1)}
              data-testid="carousel-prev"
            >
              <ChevronLeft size={22} />
            </button>
            <div className="carousel-track" ref={trackRef}>
              {SLIDES.map((s, i) => (
                <div
                  key={i}
                  className="fslide"
                  data-testid={`format-slide-${i}`}
                  role="group"
                  aria-label={s.alt}
                >
                  {s.video ? (
                    <video
                      autoPlay
                      muted
                      loop
                      playsInline
                      preload="auto"
                      poster={s.poster}
                      aria-label={s.alt}
                    >
                      {s.webm && <source src={s.webm} type="video/webm" />}
                      <source src={s.src} type="video/mp4" />
                    </video>
                  ) : (
                    <img
                      src={s.src}
                      alt={s.alt}
                      loading="lazy"
                    />
                  )}
                </div>
              ))}
            </div>
            <button
              className="carousel-btn carousel-right"
              type="button"
              aria-label="Scroll formats right"
              onClick={() => scroll(1)}
              data-testid="carousel-next"
            >
              <ChevronRight size={22} />
            </button>
          </div>
        </div>

        <div className="table-wrap" data-testid="matrix-table-wrap">
          <table className="matrix-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Features &amp; Specs</th>
                <th>Benefit Areas</th>
                <th>Applications</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r) => (
                <tr key={r.product} data-testid={`matrix-row-${r.product.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z-]/g, "")}`}>
                  <td>{r.product}</td>
                  <td data-label="Features & Specs">{r.specs.map((s, i) => (<div key={i}>{s}</div>))}</td>
                  <td data-label="Benefit Areas">{r.benefits.map((s, i) => (<div key={i}>{s}</div>))}</td>
                  <td data-label="Applications">{r.apps.map((s, i) => (<div key={i}>{s}</div>))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="buyer-advisory" data-testid="buyer-advisory">
          <div>
            <h3>Not sure which format fits your product?</h3>
            <p>
              Share your intended application, target market, certification needs and volume. We can help identify
              whether pulp, oil, powder, dried berries or finished-format support is the right route.
            </p>
          </div>
          <a className="btn btn-primary" href={MAILTO} data-testid="request-format-guidance">
            Request Format Guidance
          </a>
        </div>
      </div>
    </section>
  );
}
